import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { GOVERNANCE_AGREEMENT, colors } from '@scriptpad/shared';
import { acceptGovernance } from '../api/client.js';
import { StepPaper } from './StepPaper.js';
import type { WizardStepProps } from './WizardRouter.js';

export const Step3SoleGovernance = ({ guid, data, onAdvance, onPatched }: WizardStepProps) => {
  const mutation = useMutation({
    mutationFn: () => acceptGovernance(guid, GOVERNANCE_AGREEMENT.version),
    onSuccess: (next) => {
      onPatched(next);
      onAdvance(5);
    },
  });

  const alreadyAgreed = data.governanceAgreement?.agreed === true;

  return (
    <StepPaper>
      <Stack spacing={3}>
        <Typography variant="h3">Independent / Outpatient Prescribing Context</Typography>

        <Typography variant="body1" color="text.secondary">
          ScriptPad supports prescribing across a range of clinical settings.
        </Typography>

        <Typography variant="body1" color="text.secondary">
          If you are not operating within a configured healthcare provider organisation (such as a
          hospital or clinic system), you may prescribe within an independent or outpatient
          prescribing context available on the platform.
        </Typography>

        <Typography variant="body1" color="text.secondary">
          This environment is supported by a registered healthcare provider organisation and is
          intended for use only where it accurately reflects the care you are providing.
        </Typography>

        <Typography variant="body1" color="text.secondary">
          You should not use this context where you are practising within a healthcare provider
          organisation that has its own prescribing configuration.
        </Typography>

        <Typography variant="body1" color="text.secondary">
          All prescribing remains attributable to you as the prescriber via your HPI-I, and you
          remain solely responsible for all clinical decisions.
        </Typography>

        <Box
          sx={{
            maxHeight: 380,
            overflowY: 'auto',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            p: 3,
            bgcolor: colors.bgWash,
          }}
        >
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 700, color: colors.navy }}>
            {GOVERNANCE_AGREEMENT.title}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            Version {GOVERNANCE_AGREEMENT.version} &middot; effective {GOVERNANCE_AGREEMENT.effectiveDate}
          </Typography>
          <Stack spacing={2}>
            {GOVERNANCE_AGREEMENT.sections.map((s) => (
              <Box key={s.heading}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: colors.navy }}>
                  {s.heading}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line', mt: 0.5 }}>
                  {s.body}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Box>

        {alreadyAgreed && (
          <Alert severity="success">
            You agreed to this version on{' '}
            {new Date(data.governanceAgreement!.agreedAt).toLocaleString('en-AU')}.
          </Alert>
        )}

        {mutation.isError && <Alert severity="error">{(mutation.error as Error).message}</Alert>}

        <Stack
          direction={{ xs: 'column-reverse', sm: 'row' }}
          spacing={{ xs: 1.5, sm: 2 }}
        >
          <Button onClick={() => onAdvance(2)} variant="text">
            Back
          </Button>
          <Button
            size="large"
            variant="contained"
            disabled={mutation.isPending}
            onClick={() => (alreadyAgreed ? onAdvance(5) : mutation.mutate())}
          >
            {alreadyAgreed ? 'Continue' : 'I agree'}
          </Button>
        </Stack>
      </Stack>
    </StepPaper>
  );
};
